<template>
  <div class="profile-page">
    <div class="page-header">
      <h1 class="page-title">내 정보 수정</h1>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
    </div>

    <div v-else class="profile-form">
      <!-- 기본 정보 -->
      <section class="form-section">
        <h2 class="section-title">기본 정보</h2>
        
        <div class="info-item">
          <span class="info-label">이메일</span>
          <span class="info-value">{{ user?.email }}</span>
        </div>

        <div class="info-item">
          <span class="info-label">가입일</span>
          <span class="info-value">{{ formatDate(userProfile?.createdAt) }}</span>
        </div>

        <div class="info-item">
          <span class="info-label">회원 등급</span>
          <span class="info-value">{{ userProfile?.role === 'admin' ? '관리자' : '일반회원' }}</span>
        </div>
      </section>

      <!-- 비밀번호 변경 -->
      <section class="form-section">
        <h2 class="section-title">비밀번호 변경</h2>
        
        <div class="input-group">
          <label>새 비밀번호</label>
          <input type="password" v-model="newPassword" placeholder="6자 이상 입력" />
        </div>

        <div class="input-group">
          <label>비밀번호 확인</label>
          <input type="password" v-model="confirmPassword" placeholder="비밀번호 확인" />
        </div>

        <p v-if="passwordError" class="error-message">{{ passwordError }}</p>
        <p v-if="passwordSuccess" class="success-message">{{ passwordSuccess }}</p>

        <button class="btn-save" @click="changePassword" :disabled="changingPassword">
          {{ changingPassword ? '변경 중...' : '비밀번호 변경' }}
        </button>
      </section>

      <!-- 로그아웃 -->
      <section class="form-section danger-section">
        <h2 class="section-title">계정</h2>
        
        <button class="btn-logout" @click="handleLogout">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          로그아웃
        </button>
      </section>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { updatePassword } from 'firebase/auth'
import { useAuth } from '@/composables/useAuth'
import { auth } from '@/services/firebase'

export default {
  name: 'MyProfileView',
  setup() {
    const router = useRouter()
    const { user, userProfile, logout } = useAuth()

    const loading = ref(false)
    const newPassword = ref('')
    const confirmPassword = ref('')
    const passwordError = ref('')
    const passwordSuccess = ref('')
    const changingPassword = ref(false)

    const formatDate = (date) => {
      if (!date) return '-'
      if (date.toDate) {
        date = date.toDate()
      }
      return new Date(date).toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }

    const changePassword = async () => {
      passwordError.value = ''
      passwordSuccess.value = ''

      if (newPassword.value.length < 6) {
        passwordError.value = '비밀번호는 6자 이상이어야 합니다.'
        return
      }

      if (newPassword.value !== confirmPassword.value) {
        passwordError.value = '비밀번호가 일치하지 않습니다.'
        return
      }

      changingPassword.value = true
      try {
        await updatePassword(auth.currentUser, newPassword.value)
        passwordSuccess.value = '비밀번호가 변경되었습니다.'
        newPassword.value = ''
        confirmPassword.value = ''
      } catch (e) {
        console.error('비밀번호 변경 실패:', e)
        if (e.code === 'auth/requires-recent-login') {
          passwordError.value = '보안을 위해 다시 로그인 후 시도해주세요.'
        } else {
          passwordError.value = '비밀번호 변경에 실패했습니다.'
        }
      } finally {
        changingPassword.value = false
      }
    }

    const handleLogout = async () => {
      await logout()
      router.push('/')
    }

    return {
      user,
      userProfile,
      loading,
      newPassword,
      confirmPassword,
      passwordError,
      passwordSuccess,
      changingPassword,
      formatDate,
      changePassword,
      handleLogout
    }
  }
}
</script>

<style scoped>
.profile-page {
  max-width: 600px;
}

.page-header {
  margin-bottom: 32px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #191f28;
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: 60px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f2f4f6;
  border-top-color: #3182f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.profile-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-section {
  background: #fff;
  padding: 28px;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.section-title {
  font-size: 17px;
  font-weight: 700;
  color: #191f28;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f2f4f6;
}

.info-item {
  display: flex;
  justify-content: space-between;
  padding: 14px 0;
  border-bottom: 1px solid #f2f4f6;
}

.info-item:last-of-type {
  border-bottom: none;
}

.info-label {
  font-size: 14px;
  color: #6b7684;
}

.info-value {
  font-size: 14px;
  font-weight: 500;
  color: #191f28;
}

.input-group {
  margin-bottom: 16px;
}

.input-group label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.input-group input {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  font-size: 15px;
}

.input-group input:focus {
  outline: none;
  border-color: #3182f6;
  box-shadow: 0 0 0 3px rgba(49, 130, 246, 0.1);
}

.error-message {
  color: #e03131;
  font-size: 14px;
  margin-bottom: 16px;
}

.success-message {
  color: #12b886;
  font-size: 14px;
  margin-bottom: 16px;
}

.btn-save {
  width: 100%;
  padding: 14px;
  background: #3182f6;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.btn-save:hover:not(:disabled) {
  background: #1b64da;
}

.btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.danger-section {
  border: 1px solid #ffc9c9;
}

.btn-logout {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px;
  background: #fff;
  color: #e03131;
  border: 1px solid #e03131;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-logout:hover {
  background: #fff5f5;
}
</style>